/**
 * RecipientListSection Component
 *
 * Container component for managing email recipients.
 * Displays a list of recipients with add, edit, verify, and delete actions.
 * Uses App Bridge v4 route-based modals for add/edit operations.
 *
 * Design Compliance:
 * - Polaris Card with proper header and actions
 * - Empty state when no recipients
 * - Loading skeleton during data fetch
 * - PlusCircleIcon on Add button per design requirements
 *
 * @example
 * ```tsx
 * <RecipientListSection onRecipientsChange={handleChange} />
 * ```
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Box,
  Banner,
  SkeletonBodyText,
  Icon,
} from '@shopify/polaris';
import { PlusCircleIcon, PersonIcon } from '@shopify/polaris-icons';
import { RecipientItem, type EmailRecipient } from '../recipient-item';
import { AppBridgeModal, type ModalVariant } from '../../app-bridge/AppBridgeModal';
import { useModalRoute } from '../../../hooks/core/useModalRoute';
import { EmptyStateIllustration } from '../../ui/EmptyStateIllustration';
import { EmptyStateContainer } from '../../ui/EmptyStateContainer';
import type { ModalMessage } from '~/hooks/core/useModalCommunication';
import { authenticatedFetch } from '~/utils/authenticated-fetch';

// API Response types
interface RecipientsResponse {
  recipients?: EmailRecipient[];
}

interface RecipientResponse {
  recipient: EmailRecipient;
}

interface ErrorResponse {
  error?: string;
}

interface VerifyResponse {
  success: boolean;
  message?: string;
}

export interface RecipientListSectionProps {
  /** Optional callback when recipients change */
  onRecipientsChange?: (recipients: EmailRecipient[]) => void;
  /** Optional callback to show toast messages */
  onShowToast?: (message: string, isError?: boolean) => void;
  /** Optional callback when a recipient is deleted (provides email for cascade removal) */
  onRecipientDelete?: (emailAddress: string) => void;
}

export function RecipientListSection({
  onRecipientsChange,
  onShowToast,
  onRecipientDelete,
}: RecipientListSectionProps) {
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Route-based modal hooks
  const addRecipientModal = useModalRoute('/app/modal/add-recipient');
  const editRecipientModal = useModalRoute('/app/modal/edit-recipient');

  // Track which recipient is being edited (for message handling)
  const [editingRecipientId, setEditingRecipientId] = useState<number | null>(null);

  // Dynamic variant for edit modal (changes between edit/confirm views)
  const [editModalVariant, setEditModalVariant] = useState<ModalVariant>('base');

  // Action states
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Track newly added recipient IDs for pop animation
  const [newRecipientIds, setNewRecipientIds] = useState<Set<number>>(new Set());

  // Track IDs being animated out for deletion
  const [animatingOutIds, setAnimatingOutIds] = useState<Set<number>>(new Set());

  // Track if this is initial load (for staggered load-in animation)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Fetch recipients
  const fetchRecipients = useCallback(async () => {
    try {
      setError(null);
      const response = await authenticatedFetch('/api/recipients');
      if (!response.ok) {
        throw new Error('Failed to load recipients');
      }
      const data = (await response.json()) as RecipientsResponse;
      setRecipients(data.recipients || []);
      onRecipientsChange?.(data.recipients || []);
      // Clear initial load state after animation time
      setTimeout(() => setIsInitialLoad(false), 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipients');
      setIsInitialLoad(false);
    } finally {
      setIsLoading(false);
    }
  }, [onRecipientsChange]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  // Handle messages from Add Recipient modal
  const handleAddRecipientMessage = useCallback(
    (message: ModalMessage) => {
      if (message.type === 'recipient-added') {
        const payload = message.payload as { recipient?: EmailRecipient } | undefined;
        const recipient = payload?.recipient;
        if (recipient) {
          // Mark as new for pop animation
          setNewRecipientIds((prev) => new Set(prev).add(recipient.id));
          setTimeout(() => {
            setNewRecipientIds((prev) => {
              const next = new Set(prev);
              next.delete(recipient.id);
              return next;
            });
          }, 700);

          // Add to list
          setRecipients((prev) => {
            const updated = [...prev, recipient];
            onRecipientsChange?.(updated);
            return updated;
          });
          onShowToast?.('Recipient added. Click the email icon to send verification.');
        }
        addRecipientModal.close();
      }
    },
    [addRecipientModal, onRecipientsChange, onShowToast]
  );

  // Handle messages from Edit Recipient modal
  const handleEditRecipientMessage = useCallback(
    (message: ModalMessage) => {
      if (message.type === 'recipient-updated') {
        const payload = message.payload as { recipient?: EmailRecipient } | undefined;
        const recipient = payload?.recipient;
        if (recipient) {
          setRecipients((prev) => {
            const updated = prev.map((r) => (r.id === recipient.id ? recipient : r));
            onRecipientsChange?.(updated);
            return updated;
          });
          onShowToast?.('Recipient updated');
        }
        editRecipientModal.close();
        setEditingRecipientId(null);
      } else if (message.type === 'request-variant-change') {
        // Handle dynamic variant change for edit/confirm view transitions
        const payload = message.payload as { variant?: ModalVariant } | undefined;
        if (payload?.variant) {
          setEditModalVariant(payload.variant);
        }
      }
    },
    [editRecipientModal, onRecipientsChange, onShowToast]
  );

  // Toggle channel on/off for a recipient (inline toggle)
  const handleToggleChannel = useCallback(
    async (
      recipient: EmailRecipient,
      channelType: 'digest' | 'instant',
      enabled: boolean
    ): Promise<boolean> => {
      try {
        const response = await authenticatedFetch(
          `/api/recipients/${recipient.id}/channels/${channelType}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled }),
          }
        );

        if (!response.ok) {
          const errorData = (await response.json()) as ErrorResponse;
          onShowToast?.(errorData.error || 'Failed to update channel', true);
          return false;
        }

        const result = (await response.json()) as RecipientResponse;

        // Update recipient in local state using functional update
        setRecipients((prev) => {
          const updated = prev.map((r) => (r.id === recipient.id ? result.recipient : r));
          onRecipientsChange?.(updated);
          return updated;
        });

        const channelName = channelType === 'digest' ? 'Weekly digest' : 'Threshold alerts';
        onShowToast?.(`${channelName} ${enabled ? 'enabled' : 'disabled'}`);
        return true;
      } catch (err) {
        onShowToast?.(
          err instanceof Error ? err.message : 'Failed to update channel',
          true
        );
        return false;
      }
    },
    [onRecipientsChange, onShowToast]
  );

  // Start delete animation (triggers API call after animation)
  const handleDeleteRecipient = useCallback(
    (recipient: EmailRecipient) => {
      // Start the animation
      setAnimatingOutIds((prev) => new Set(prev).add(recipient.id));
      setDeletingId(recipient.id);
    },
    []
  );

  // Actually remove recipient after animation completes
  const handleDeleteAnimationComplete = useCallback(
    async (recipient: EmailRecipient) => {
      try {
        const response = await authenticatedFetch(`/api/recipients/${recipient.id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const errorData = (await response.json()) as ErrorResponse;
          throw new Error(errorData.error || 'Failed to delete recipient');
        }

        // Remove from list using functional update
        setRecipients((prev) => {
          const updated = prev.filter((r) => r.id !== recipient.id);
          onRecipientsChange?.(updated);
          return updated;
        });

        // Notify parent to remove associated channels (for UI animation)
        onRecipientDelete?.(recipient.email_address);

        onShowToast?.('Recipient deleted');
      } catch (err) {
        // On error, restore the item by clearing animation state
        setAnimatingOutIds((prev) => {
          const next = new Set(prev);
          next.delete(recipient.id);
          return next;
        });
        setError(err instanceof Error ? err.message : 'Failed to delete recipient');
      } finally {
        setDeletingId(null);
        setAnimatingOutIds((prev) => {
          const next = new Set(prev);
          next.delete(recipient.id);
          return next;
        });
      }
    },
    [onRecipientsChange, onShowToast, onRecipientDelete]
  );

  // Send verification email - returns success boolean
  const handleSendVerification = useCallback(
    async (recipient: EmailRecipient): Promise<boolean> => {
      try {
        const response = await authenticatedFetch(
          `/api/recipients/${recipient.id}/send-verification`,
          { method: 'POST' }
        );

        if (!response.ok) {
          const errorData = (await response.json()) as ErrorResponse;
          onShowToast?.(errorData.error || 'Failed to send verification email', true);
          return false;
        }

        onShowToast?.('Verification code sent to ' + recipient.email_address);
        return true;
      } catch (err) {
        onShowToast?.(
          err instanceof Error ? err.message : 'Failed to send verification email',
          true
        );
        return false;
      }
    },
    [onShowToast]
  );

  // Verify code - returns success boolean
  const handleVerifyCode = useCallback(
    async (recipient: EmailRecipient, code: string): Promise<boolean> => {
      try {
        const response = await authenticatedFetch(`/api/recipients/${recipient.id}/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });

        const data = (await response.json()) as VerifyResponse & ErrorResponse;

        if (!response.ok) {
          // Don't show toast for wrong code - UI handles it
          if (response.status === 400 && data.error?.includes('Invalid')) {
            return false;
          }
          onShowToast?.(data.error || 'Failed to verify code', true);
          return false;
        }

        // Update recipient in local state to show verified
        setRecipients((prev) =>
          prev.map((r) =>
            r.id === recipient.id
              ? { ...r, is_verified: true, verified_at: new Date().toISOString() }
              : r
          )
        );
        onShowToast?.('Email verified successfully');
        return true;
      } catch (err) {
        onShowToast?.(
          err instanceof Error ? err.message : 'Failed to verify code',
          true
        );
        return false;
      }
    },
    [onShowToast]
  );

  // Send test email - returns success boolean
  const handleTestRecipient = useCallback(
    async (recipient: EmailRecipient): Promise<boolean> => {
      try {
        const response = await authenticatedFetch(`/api/recipients/${recipient.id}/test`, {
          method: 'POST',
        });

        if (!response.ok) {
          const errorData = (await response.json()) as ErrorResponse;
          onShowToast?.(errorData.error || 'Failed to send test email', true);
          return false;
        }

        onShowToast?.('Test email sent to ' + recipient.email_address);
        return true;
      } catch (err) {
        onShowToast?.(
          err instanceof Error ? err.message : 'Failed to send test email',
          true
        );
        return false;
      }
    },
    [onShowToast]
  );

  // Open add modal
  const handleOpenAdd = useCallback(() => {
    addRecipientModal.open();
  }, [addRecipientModal]);

  // Open edit modal
  const handleOpenEdit = useCallback(
    (recipient: EmailRecipient) => {
      setEditingRecipientId(recipient.id);
      editRecipientModal.open({ recipientId: String(recipient.id) });
    },
    [editRecipientModal]
  );

  // Close handlers
  const handleCloseAddModal = useCallback(() => {
    addRecipientModal.close();
  }, [addRecipientModal]);

  const handleCloseEditModal = useCallback(() => {
    editRecipientModal.close();
    setEditingRecipientId(null);
    // Reset variant to default when modal closes
    setEditModalVariant('base');
  }, [editRecipientModal]);

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Icon source={PersonIcon} tone="subdued" />
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Email Recipients
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Manage email addresses for notifications
                </Text>
              </BlockStack>
            </InlineStack>
          </InlineStack>
          <SkeletonBodyText lines={3} />
        </BlockStack>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <BlockStack gap="400">
          {/* Header */}
          <InlineStack align="space-between" blockAlign="start">
            <InlineStack gap="200" blockAlign="start">
              <Box paddingBlockStart="050">
                <Icon source={PersonIcon} tone="subdued" />
              </Box>
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Email Recipients
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Add and verify email addresses to use in notification channels
                </Text>
              </BlockStack>
            </InlineStack>
            {recipients.length > 0 && (
              <Button
                icon={PlusCircleIcon}
                onClick={handleOpenAdd}
              >
                Add recipient
              </Button>
            )}
          </InlineStack>

          {/* Error banner */}
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <Text as="p" variant="bodyMd">
                {error}
              </Text>
            </Banner>
          )}

          {/* Empty state */}
          {recipients.length === 0 ? (
            <Box
              background="bg-surface-secondary"
              padding="400"
              borderRadius="200"
            >
              <EmptyStateContainer size="sm">
                <BlockStack gap="300" align="center">
                  <EmptyStateIllustration variant="email-recipients" width={120} height={96} />
                  <Text as="p" variant="bodyMd" fontWeight="semibold" alignment="center">
                    No email recipients yet
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                    Add email addresses to receive inventory alerts and
                    notifications. Recipients must verify their email before
                    receiving messages.
                  </Text>
                  <InlineStack align="center">
                    <Button size="slim" icon={PlusCircleIcon} onClick={handleOpenAdd}>
                      Add recipient
                    </Button>
                  </InlineStack>
                </BlockStack>
              </EmptyStateContainer>
            </Box>
          ) : (
            /* Recipients list */
            <div className="recipients-list">
              <BlockStack gap="300">
                {recipients.map((recipient) => (
                  <RecipientItem
                    key={recipient.id}
                    recipient={recipient}
                    onSendVerification={handleSendVerification}
                    onVerifyCode={handleVerifyCode}
                    onTest={handleTestRecipient}
                    onEdit={handleOpenEdit}
                    onDelete={handleDeleteRecipient}
                    onToggleChannel={handleToggleChannel}
                    isDeleting={deletingId === recipient.id}
                    isNew={newRecipientIds.has(recipient.id)}
                    isAnimatingOut={animatingOutIds.has(recipient.id)}
                    onDeleteAnimationComplete={handleDeleteAnimationComplete}
                    isLoadingIn={isInitialLoad && !newRecipientIds.has(recipient.id)}
                  />
                ))}
              </BlockStack>
            </div>
          )}
        </BlockStack>
      </Card>

      {/* Add Recipient Modal - App Bridge route-based */}
      <AppBridgeModal
        src={addRecipientModal.src}
        open={addRecipientModal.isOpen}
        onClose={handleCloseAddModal}
        variant="base"
        onMessage={handleAddRecipientMessage}
      />

      {/* Edit Recipient Modal - App Bridge route-based with dynamic variant */}
      <AppBridgeModal
        src={editRecipientModal.src}
        open={editRecipientModal.isOpen}
        onClose={handleCloseEditModal}
        variant={editModalVariant}
        onMessage={handleEditRecipientMessage}
      />
    </>
  );
}
